import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { SmileyLoaderComponent } from '@shared/components/smiley-loader/smiley-loader.component';

/**
 * Where the loader is shown. Drives positioning and accessibility.
 *  - `inline`  — in normal flow, decorative; drop into an already-labelled region.
 *  - `section` — centred, filling its container block.
 *  - `overlay` — centred over the nearest positioned ancestor (non-blocking).
 *  - `page`    — fixed, centred in the main content area (offset by the app shell's
 *               sidebar via `--fb-app-content-offset`; full-viewport outside the shell).
 */
export type FoodbackLoaderVariant = 'inline' | 'section' | 'overlay' | 'page';

/** Default loader size (px) per variant; overridable via the `size` input. */
const VARIANT_SIZE: Record<FoodbackLoaderVariant, number> = {
  inline: 24,
  section: 64,
  overlay: 96,
  page: 96,
};

/**
 * <app-foodback-loader> — the standard Foodback loading indicator.
 *
 * Wraps <app-smiley-loader> with positioning, a slightly see-through card and a
 * loading label for the common loading contexts (see {@link FoodbackLoaderVariant}).
 * Container variants are non-blocking (no full-page backdrop) and announce
 * themselves as a `role="status"` live region; the `inline` variant is decorative.
 */
@Component({
  selector: 'app-foodback-loader',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SmileyLoaderComponent],
  template: `
    <div class="foodback-loader__inner">
      <app-smiley-loader
        aria-hidden="true"
        [size]="resolvedSize()"
        [speed]="speed()"
        [color]="color()" />
      @if (variant() !== 'inline') {
        <span class="foodback-loader__label" [style.color]="container() ? null : color()">{{
          label()
        }}</span>
      }
    </div>
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
      /* on-surface, not primary: in the light theme the two are the same brand navy, but primary
         lightens to a pale lavender in dark, which leaves the mark washed out on the card it sits
         on. The boot splash passes its own colour, so it is unaffected. */
      color: var(--mat-sys-on-surface, #1b1d3e);
    }
    :host(.foodback-loader--inline) {
      display: inline-flex;
    }
    :host(.foodback-loader--section) {
      width: 100%;
      height: 100%;
      min-height: 8rem;
    }
    :host(.foodback-loader--overlay) {
      position: absolute;
      inset: 0;
      z-index: 4; /* pagination sits at z-index 3 */
      pointer-events: none;
    }
    :host(.foodback-loader--page) {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      /* Offset by the app shell's sidebar width so the loader centres in the main
         content area; falls back to 0 outside the shell (e.g. the boot splash). */
      left: var(--fb-app-content-offset, 0);
      z-index: 5;
      pointer-events: none;
    }
    .foodback-loader__inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;
    }
    /* Slightly see-through card that floats over the content/skeleton. The fill is a token
       because the step it needs is not the same one in both themes. */
    :host(.foodback-loader--card) .foodback-loader__inner {
      padding: 30px 50px;
      border-radius: 24px;
      background: var(--fb-loader-card);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
      /* Host stays non-blocking (doesn't cover the page); the card itself is solid. */
      pointer-events: auto;
    }
    .foodback-loader__label {
      font-size: 0.875rem;
      color: var(--mat-sys-on-surface-variant);
      white-space: nowrap;
    }
  `,
  host: {
    class: 'foodback-loader',
    '[class.foodback-loader--inline]': "variant() === 'inline'",
    '[class.foodback-loader--section]': "variant() === 'section'",
    '[class.foodback-loader--overlay]': "variant() === 'overlay'",
    '[class.foodback-loader--page]': "variant() === 'page'",
    '[class.foodback-loader--card]': "container() && variant() !== 'inline'",
    '[attr.role]': "variant() === 'inline' ? null : 'status'",
    '[attr.aria-live]': "variant() === 'inline' ? null : 'polite'",
    '[attr.aria-hidden]': "variant() === 'inline' ? 'true' : null",
  },
})
export class FoodbackLoaderComponent {
  /** Presentation context — see {@link FoodbackLoaderVariant}. */
  readonly variant = input<FoodbackLoaderVariant>('inline');
  /** Loading text shown and announced by the live region (container variants). */
  readonly label = input('Loading...');
  /** Wrap the loader in a slightly see-through card. Set false for a bare loader (no card). */
  readonly container = input(true);
  /** Loader size in px. Defaults to a sensible size per variant. */
  readonly size = input<number | undefined>(undefined);
  /** Animation playback multiplier, forwarded to the smiley loader. */
  readonly speed = input(1);
  /** Colour of the loader (any CSS colour). */
  readonly color = input('currentColor');

  protected readonly resolvedSize = computed(() => this.size() ?? VARIANT_SIZE[this.variant()]);
}
